import { createSlice } from "@reduxjs/toolkit";
import {
  getConstructionsByInstitution,
  getConstructionsByInstitutionPatch,
  getConstructionById,
  getConstructionLogs,
  addConstruction,
  editConstruction,
  deleteConstruction,
  bulkUploadConstructions,
  bulkUpdateConstructions,
  getConstructionSummaryReport,
  getConstructionMatrixByInstitution,
  getConstructionMatrixByInstitutionWithAiSearch
} from "../actions/constructions";
import { logoutUser } from "../actions/account";

const initialState = {
  error: false,
  isLoadingConstructions: false,
  isLoadingConstructionById: false,
  isLoadingConstructionLogs: false,
  isLoadingAddConstruction: false,
  isLoadingEditConstruction: false,
  isLoadingDeleteConstruction: false,
  isLoadingBulkUploadConstruction: false,
  isLoadingBulkUpdateConstruction: false,
  isLoadingConstructionSummaryReport: false,
  constructionSummaryReportError: false,
  isLoadingConstructionMatrix: false,
  constructionMatrixRows: [],
  constructionMatrixTotal: 0,
  constructionMatrixSummary: {},
  constructionMatrixColumns: [],
  constructionMatrixCohorts: [],
  constructionMatrixAiSearch: null,
  constructions: [],
  total: 0,
  summary: {
    moveRequiredCount: 0,
    noMoveCount: 0,
    occupiedCount: 0,
    unoccupiedCount: 0,
  },
  constructionDetails: {},
  constructionLogs: [],
  constructionLogsTotal: 0,
  constructionSummaryReport: {
    cohortCount: 0,
    cohorts: [],
  },
  bulkUploadSummary: null,
  bulkUpdateSummary: null
};

const constructionsSlice = createSlice({
  name: "construction",
  initialState,
  reducers: {
    clearConstructionDetails(state) {
      state.constructionDetails = {};
    },
    clearConstructionLogs(state) {
      state.constructionLogs = [];
      state.constructionLogsTotal = 0;
    },
  },
  extraReducers: {
    [getConstructionsByInstitution.pending]: (state) => {
      state.isLoadingConstructions = true;
      state.error = false;
    },
    [getConstructionsByInstitution.fulfilled]: (state, action) => {
      state.isLoadingConstructions = false;
      state.constructions = action?.payload?.data?.constructions || [];
      state.total = action?.payload?.data?.total || 0;
      state.summary = action?.payload?.data?.summary || initialState.summary;
      state.error = false;
    },
    [getConstructionsByInstitution.rejected]: (state) => {
      state.isLoadingConstructions = false;
      state.error = true;
    },
    [getConstructionsByInstitutionPatch.pending]: (state) => {
      state.isLoadingConstructions = true;
      state.error = false;
    },
    [getConstructionsByInstitutionPatch.fulfilled]: (state, action) => {
      state.isLoadingConstructions = false;
      state.constructions = action?.payload?.data?.constructions || [];
      state.total = action?.payload?.data?.total || 0;
      state.summary = action?.payload?.data?.summary || initialState.summary;
      state.error = false;
    },
    [getConstructionsByInstitutionPatch.rejected]: (state) => {
      state.isLoadingConstructions = false;
      state.error = true;
    },
    [getConstructionById.pending]: (state) => {
      state.isLoadingConstructionById = true;
      state.error = false;
    },
    [getConstructionById.fulfilled]: (state, action) => {
      state.isLoadingConstructionById = false;
      state.constructionDetails = action?.payload?.data?.construction || {};
      state.error = false;
    },
    [getConstructionById.rejected]: (state) => {
      state.isLoadingConstructionById = false;
      state.error = true;
    },
    [getConstructionLogs.pending]: (state) => {
      state.isLoadingConstructionLogs = true;
      state.error = false;
    },
    [getConstructionLogs.fulfilled]: (state, action) => {
      state.isLoadingConstructionLogs = false;
      state.constructionLogs = action?.payload?.data?.logs || [];
      state.constructionLogsTotal = action?.payload?.data?.total || 0;
      state.error = false;
    },
    [getConstructionLogs.rejected]: (state) => {
      state.isLoadingConstructionLogs = false;
      state.error = true;
    },
    [addConstruction.pending]: (state) => {
      state.isLoadingAddConstruction = true;
      state.error = false;
    },
    [addConstruction.fulfilled]: (state, action) => {
      state.isLoadingAddConstruction = false;
      state.constructionDetails = action?.payload?.data?.construction || {};
      state.error = false;
    },
    [addConstruction.rejected]: (state) => {
      state.isLoadingAddConstruction = false;
      state.error = true;
    },
    [editConstruction.pending]: (state) => {
      state.isLoadingEditConstruction = true;
      state.error = false;
    },
    [editConstruction.fulfilled]: (state, action) => {
      state.isLoadingEditConstruction = false;
      state.constructionDetails = action?.payload?.data?.construction || state.constructionDetails;
      state.error = false;
    },
    [editConstruction.rejected]: (state) => {
      state.isLoadingEditConstruction = false;
      state.error = true;
    },
    [deleteConstruction.pending]: (state) => {
      state.isLoadingDeleteConstruction = true;
      state.error = false;
    },
    [deleteConstruction.fulfilled]: (state) => {
      state.isLoadingDeleteConstruction = false;
      state.error = false;
    },
    [deleteConstruction.rejected]: (state) => {
      state.isLoadingDeleteConstruction = false;
      state.error = true;
    },
    [bulkUploadConstructions.pending]: (state) => {
      state.isLoadingBulkUploadConstruction = true;
      state.error = false;
    },
    [bulkUploadConstructions.fulfilled]: (state, action) => {
      state.isLoadingBulkUploadConstruction = false;
      state.bulkUploadSummary = action?.payload?.data || null;
      state.error = false;
    },
    [bulkUploadConstructions.rejected]: (state) => {
      state.isLoadingBulkUploadConstruction = false;
      state.error = true;
    },
    [bulkUpdateConstructions.pending]: (state) => {
      state.isLoadingBulkUpdateConstruction = true;
      state.error = false;
    },
    [bulkUpdateConstructions.fulfilled]: (state, action) => {
      state.isLoadingBulkUpdateConstruction = false;
      state.bulkUpdateSummary = action?.payload?.data || null;
      state.error = false;
    },
    [bulkUpdateConstructions.rejected]: (state) => {
      state.isLoadingBulkUpdateConstruction = false;
      state.error = true;
    },
    [getConstructionSummaryReport.pending]: (state) => {
      state.isLoadingConstructionSummaryReport = true;
      state.constructionSummaryReportError = false;
    },
    [getConstructionSummaryReport.fulfilled]: (state, action) => {
      state.isLoadingConstructionSummaryReport = false;
      state.constructionSummaryReport = action?.payload?.data || initialState.constructionSummaryReport;
      state.constructionSummaryReportError = false;
    },
    [getConstructionSummaryReport.rejected]: (state) => {
      state.isLoadingConstructionSummaryReport = false;
      state.constructionSummaryReportError = true;
    },
    [getConstructionMatrixByInstitution.pending]: (state) => {
      state.isLoadingConstructionMatrix = true;
      state.error = false;
    },
    [getConstructionMatrixByInstitution.fulfilled]: (state, action) => {
      state.isLoadingConstructionMatrix = false;
      state.constructionMatrixRows = action?.payload?.data?.rows || [];
      state.constructionMatrixColumns = action?.payload?.data?.columns || [];
      state.constructionMatrixTotal = action?.payload?.data?.total || 0;
      state.constructionMatrixSummary = action?.payload?.data?.summary || {};
      state.constructionMatrixCohorts = action?.payload?.data?.cohorts || [];
      state.constructionMatrixAiSearch = action?.payload?.data?.aiSearch || null;
      state.error = false;
    },
    [getConstructionMatrixByInstitution.rejected]: (state) => {
      state.isLoadingConstructionMatrix = false;
      state.error = true;
    },
    [getConstructionMatrixByInstitutionWithAiSearch.pending]: (state) => {
      state.isLoadingConstructionMatrix = true;
      state.error = false;
    },
    [getConstructionMatrixByInstitutionWithAiSearch.fulfilled]: (state, action) => {
      state.isLoadingConstructionMatrix = false;
      state.constructionMatrixRows = action?.payload?.data?.rows || [];
      state.constructionMatrixColumns = action?.payload?.data?.columns || [];
      state.constructionMatrixTotal = action?.payload?.data?.total || 0;
      state.constructionMatrixSummary = action?.payload?.data?.summary || {};
      state.constructionMatrixCohorts = action?.payload?.data?.cohorts || [];
      state.constructionMatrixAiSearch = action?.payload?.data?.aiSearch || null;
      state.error = false;
    },
    [getConstructionMatrixByInstitutionWithAiSearch.rejected]: (state) => {
      state.isLoadingConstructionMatrix = false;
      state.error = true;
    },
    [logoutUser.fulfilled]: () => initialState,
    [logoutUser.rejected]: () => initialState
  }
});

export const getConstructionError = (state) => state.construction.error;
export const getIsLoadingConstructions = (state) => state.construction.isLoadingConstructions;
export const getConstructionList = (state) => state.construction.constructions;
export const getConstructionTotal = (state) => state.construction.total;
export const getConstructionSummary = (state) => state.construction.summary;
export const getConstructionDetails = (state) => state.construction.constructionDetails;
export const getIsLoadingConstructionById = (state) => state.construction.isLoadingConstructionById;
export const getConstructionLogsList = (state) => state.construction.constructionLogs;
export const getConstructionLogsTotal = (state) => state.construction.constructionLogsTotal;
export const getIsLoadingConstructionLogs = (state) => state.construction.isLoadingConstructionLogs;
export const getIsLoadingAddConstruction = (state) => state.construction.isLoadingAddConstruction;
export const getIsLoadingEditConstruction = (state) => state.construction.isLoadingEditConstruction;
export const getIsLoadingDeleteConstruction = (state) => state.construction.isLoadingDeleteConstruction;
export const getIsLoadingBulkUploadConstruction = (state) => state.construction.isLoadingBulkUploadConstruction;
export const getConstructionBulkUploadSummary = (state) => state.construction.bulkUploadSummary;
export const getIsLoadingBulkUpdateConstruction = (state) => state.construction.isLoadingBulkUpdateConstruction;
export const getConstructionBulkUpdateSummary = (state) => state.construction.bulkUpdateSummary;
export const getIsLoadingConstructionSummaryReport = (state) => state.construction.isLoadingConstructionSummaryReport;
export const getConstructionSummaryReportData = (state) => state.construction.constructionSummaryReport;
export const getConstructionSummaryReportError = (state) => state.construction.constructionSummaryReportError;
export const getConstructionMatrixRows = (state) => state.construction.constructionMatrixRows;
export const getConstructionMatrixSummary = (state) => state.construction.constructionMatrixSummary;
export const getConstructionMatrixTotal = (state) => state.construction.constructionMatrixTotal;
export const getConstructionMatrixCohorts = (state) => state.construction.constructionMatrixCohorts;
export const getIsLoadingConstructionMatrix = (state) => state.construction.isLoadingConstructionMatrix;
export const { clearConstructionDetails, clearConstructionLogs } = constructionsSlice.actions;
export const getConstructionMatrixColumns = (state) => state.construction.constructionMatrixColumns;
export const getConstructionMatrixAiSearch = (state) => state.construction.constructionMatrixAiSearch;

export default constructionsSlice;
